import axios from 'axios';

const POLL_INTERVAL_MS = 2000;
/** Allow long recordings + queue backlog + Whisper latency */
const MAX_WAIT_MS = 180000;

/** Calm, recoverable copy shown when transcription cannot finish. */
export const TRANSCRIPTION_USER_FAILURE_MESSAGE =
  'We could not transcribe this audio. You can try another file or type the memory manually.';

function httpDetail(data) {
  if (!data || typeof data !== 'object') return null;
  const d = data.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object' && typeof d.message === 'string') return d.message;
  if (Array.isArray(d) && d[0]?.msg) return d.map((x) => x.msg).join(' ');
  return null;
}

/** Safe machine code from FastAPI detail object (never secrets). */
export function httpDetailCode(data) {
  if (!data || typeof data !== 'object') return null;
  const d = data.detail;
  if (d && typeof d === 'object' && typeof d.code === 'string') return d.code;
  return null;
}

function transcribeRequestError(err) {
  const fromBody = httpDetail(err?.response?.data);
  if (fromBody) return fromBody;
  if (err?.message) return err.message;
  return 'Transcription request failed';
}

/**
 * POST /stories/:id/transcribe returns 202 + queued job; transcript is written by the worker.
 * Poll GET /stories/:id until transcription_status is completed or failed.
 *
 * @param {string} API - Base URL including /api (e.g. http://localhost:8000/api)
 * @param {string} storyId
 * @param {object} [options]
 * @param {(phase: string) => void} [options.onPhase] — 'starting' | 'queued' | 'transcribing' | 'completed' | 'failed'
 * @param {(data: object) => void} [options.onQueued]
 * @param {() => void} [options.onPoll]
 */
export async function transcribeStoryAndWait(API, storyId, options = {}) {
  const { onQueued, onPoll, onPhase, onPollStory } = options;

  const devLog = (msg, extra) => {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.debug('[transcribe]', msg, extra ?? '');
    }
  };

  const transcribeUrl = `${API}/stories/${storyId}/transcribe`;
  devLog('request URL', transcribeUrl);

  onPhase?.('starting');
  let postResp;
  try {
    postResp = await axios.post(transcribeUrl);
  } catch (err) {
    devLog('POST error', { status: err?.response?.status, data: err?.response?.data });
    onPhase?.('failed');
    throw new Error(transcribeRequestError(err));
  }

  devLog('POST response', { status: postResp.status, jobId: postResp.data?.job_id });

  if (postResp.status === 200 && postResp.data?.transcript != null) {
    onPhase?.('completed');
    return String(postResp.data.transcript);
  }

  if (postResp.status !== 202) {
    const detail = httpDetail(postResp.data) || 'Transcription request failed';
    onPhase?.('failed');
    throw new Error(detail);
  }

  if (typeof onQueued === 'function') {
    onQueued(postResp.data);
  }
  onPhase?.('queued');

  const deadline = Date.now() + MAX_WAIT_MS;
  let pollIdx = 0;

  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    pollIdx += 1;
    if (typeof onPoll === 'function') {
      onPoll();
    }

    let cur;
    try {
      const { data } = await axios.get(`${API}/stories/${storyId}`);
      cur = data;
      if (typeof onPollStory === 'function') {
        onPollStory(data);
      }
    } catch (err) {
      devLog('poll GET error', err?.message);
      continue;
    }

    const status = cur.transcription_status;
    devLog('poll', { pollIdx, transcription_status: status, rawLen: (cur.raw_transcript || '').length });

    if (status === 'processing') {
      onPhase?.('transcribing');
    } else if (status === 'queued' || status === 'pending') {
      onPhase?.('queued');
    } else if (!status || status === 'idle') {
      onPhase?.('queued');
    }

    if (status === 'completed') {
      onPhase?.('completed');
      return cur.raw_transcript ?? '';
    }
    if (status === 'failed') {
      const base =
        (typeof cur.transcription_error === 'string' && cur.transcription_error.trim()) ||
        'Transcription failed. Check that OPENAI_API_KEY is valid and the ARQ worker is running.';
      const code =
        typeof cur.transcription_failure_code === 'string' && cur.transcription_failure_code.trim()
          ? cur.transcription_failure_code.trim()
          : null;
      const errMsg = code ? `${base} (${code})` : base;
      onPhase?.('failed');
      throw new Error(errMsg);
    }
  }

  onPhase?.('failed');
  throw new Error(TRANSCRIPTION_USER_FAILURE_MESSAGE);
}

function segmentFromStoryPayload(storyPayload, segmentId) {
  const list = storyPayload?.audio_segments;
  if (!Array.isArray(list)) return null;
  return list.find((s) => s.id === segmentId) || null;
}

/**
 * POST /stories/:id/audio/segments/:segmentId/transcribe — poll until segment completes.
 * Returns merged story raw_transcript after append.
 */
export async function transcribeStorySegmentAndWait(API, storyId, segmentId, options = {}) {
  const { onQueued, onPoll, onPhase, onPollStory } = options;
  const transcribeUrl = `${API}/stories/${storyId}/audio/segments/${segmentId}/transcribe`;

  onPhase?.('starting');
  let postResp;
  try {
    postResp = await axios.post(transcribeUrl);
  } catch (err) {
    onPhase?.('failed');
    throw new Error(transcribeRequestError(err));
  }

  if (postResp.status !== 202) {
    onPhase?.('failed');
    throw new Error(httpDetail(postResp.data) || 'Transcription request failed');
  }

  if (typeof onQueued === 'function') {
    onQueued(postResp.data);
  }
  onPhase?.('queued');

  const deadline = Date.now() + MAX_WAIT_MS;
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    if (typeof onPoll === 'function') {
      onPoll();
    }

    let cur;
    try {
      const { data } = await axios.get(`${API}/stories/${storyId}`);
      cur = data;
      if (typeof onPollStory === 'function') {
        onPollStory(data);
      }
    } catch {
      continue;
    }

    const seg = segmentFromStoryPayload(cur, segmentId);
    const status = seg?.transcription_status;
    if (status === 'processing') {
      onPhase?.('transcribing');
    } else if (status === 'queued' || status === 'pending') {
      onPhase?.('queued');
    } else if (!status || status === 'idle') {
      onPhase?.('queued');
    }

    if (status === 'completed') {
      onPhase?.('completed');
      return cur.raw_transcript ?? '';
    }
    if (status === 'failed') {
      const base =
        (typeof seg?.transcription_error === 'string' && seg.transcription_error.trim()) ||
        'Transcription failed. Check that OPENAI_API_KEY is valid and the ARQ worker is running.';
      const code =
        typeof seg?.transcription_failure_code === 'string' && seg.transcription_failure_code.trim()
          ? seg.transcription_failure_code.trim()
          : null;
      onPhase?.('failed');
      throw new Error(code ? `${base} (${code})` : base);
    }
  }

  onPhase?.('failed');
  throw new Error(TRANSCRIPTION_USER_FAILURE_MESSAGE);
}
